import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { BASE_URL } from "../../Utils/api";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import "./GodMode.css";


const BADGES_STATUS = {
  NAO_INTEGRADO: "secondary",
  PENDENTE: "warning",
  SINCRONIZADO: "success",
  ERRO: "danger",
  INATIVO: "dark",
};

function formatarDataHora(valor) {
  if (!valor) return "—";
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? String(valor)
    : data.toLocaleString("pt-BR");
}

function nomeEntidade(entidade) {
  return entidade.fantasia || entidade.razao_social || `Entidade #${entidade.id}`;
}

function rotuloStatusIntegracao(status) {
  if (status === "NAO_INTEGRADO" || status === "PENDENTE") return "Pendente";
  return String(status || "Pendente").replace(/_/g, " ");
}

function ClientesGodMode() {
  const token = localStorage.getItem("authToken");
  const [ambiente, setAmbiente] = useState("DESENVOLVIMENTO");
  const [entidades, setEntidades] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [limite] = useState(50);
  const [paginacao, setPaginacao] = useState(null);
  const [configurado, setConfigurado] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testando, setTestando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [busca, setBusca] = useState("");
  const [buscaDebounced, setBuscaDebounced] = useState("");
  const [filtroSituacao, setFiltroSituacao] = useState("");
  const [selecionada, setSelecionada] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [entidadeParaExcluir, setEntidadeParaExcluir] = useState(null);
  const [excluindo, setExcluindo] = useState(false);
  const [bloqueioFinanceiroExclusao, setBloqueioFinanceiroExclusao] =
    useState(null);
  const [cancelandoFinanceiro, setCancelandoFinanceiro] = useState(false);
  const [preferencias, setPreferencias] = useState({
    notificacoes_desabilitadas: true,
    emails_adicionais: "",
    nome_grupo: "",
  });

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const total = paginacao?.total ?? 0;
  const totalPaginas = paginacao?.totalPaginas ?? 0;

  useEffect(() => {
    const timer = setTimeout(() => setBuscaDebounced(busca.trim()), 400);
    return () => clearTimeout(timer);
  }, [busca]);

  useEffect(() => {
    setPagina(1);
  }, [ambiente, filtroSituacao, buscaDebounced]);

  const carregar = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErro("");
    try {
      const params = { ambiente, pagina, limite };
      if (filtroSituacao) params.situacao = filtroSituacao;
      if (buscaDebounced) params.busca = buscaDebounced;

      const { data } = await api.get(
        `${BASE_URL}/api/godmode/integracoes/asaas/entidades`,
        { headers, params }
      );
      setEntidades(Array.isArray(data?.data) ? data.data : []);
      setPaginacao(data?.paginacao ?? null);
      setConfigurado(Boolean(data?.configurado));
    } catch (err) {
      setEntidades([]);
      setPaginacao(null);
      setErro(
        err.response?.data?.message ||
          "Não foi possível carregar as entidades para integração."
      );
    } finally {
      setLoading(false);
    }
  }, [
    ambiente,
    buscaDebounced,
    filtroSituacao,
    headers,
    limite,
    pagina,
    token,
  ]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirSincronizacao = (entidade) => {
    setSelecionada(entidade);
    setPreferencias({
      notificacoes_desabilitadas:
        entidade.notificacoes_desabilitadas == null
          ? true
          : Boolean(entidade.notificacoes_desabilitadas),
      emails_adicionais: entidade.emails_adicionais || "",
      nome_grupo:
        entidade.nome_grupo ||
        entidade.fantasia ||
        entidade.razao_social ||
        "",
    });
    setErro("");
    setSucesso("");
  };

  const toggleAbonada = async (entidade) => {
    const novoValor = entidade.mensalidade_abonada !== "S";
    try {
      const { data } = await api.patch(
        `${BASE_URL}/api/godmode/entidades/${entidade.id}/mensalidade-abonada`,
        { mensalidade_abonada: novoValor },
        { headers }
      );
      const mensalidadeAbonada = data?.mensalidade_abonada || (novoValor ? "S" : "N");
      setEntidades((prev) =>
        prev.map((e) =>
          e.id === entidade.id
            ? { ...e, mensalidade_abonada: mensalidadeAbonada }
            : e
        )
      );
    } catch (err) {
      setErro(err.response?.data?.message || "Erro ao atualizar mensalidade abonada.");
    }
  };

  const sincronizar = async () => {
    if (!selecionada) return;
    setSalvando(true);
    setErro("");
    setSucesso("");
    try {
      const { data } = await api.post(
        `${BASE_URL}/api/godmode/integracoes/asaas/entidades/${selecionada.id}/sincronizar`,
        { ambiente, ...preferencias },
        { headers }
      );
      setSucesso(data?.message || "Cliente sincronizado com sucesso.");
      setSelecionada(null);
      await carregar();
    } catch (err) {
      setErro(
        err.response?.data?.message ||
          "Não foi possível sincronizar o cliente com o Asaas."
      );
    } finally {
      setSalvando(false);
    }
  };

  const abrirExclusao = (entidade) => {
    setEntidadeParaExcluir(entidade);
    setBloqueioFinanceiroExclusao(null);
    setErro("");
    setSucesso("");
  };

  const fecharExclusao = () => {
    if (excluindo || cancelandoFinanceiro) return;
    setEntidadeParaExcluir(null);
    setBloqueioFinanceiroExclusao(null);
  };

  const excluirEntidade = async () => {
    if (!entidadeParaExcluir) return;
    setExcluindo(true);
    setErro("");
    setSucesso("");
    try {
      const { data } = await api.delete(
        `${BASE_URL}/api/godmode/entidades/${entidadeParaExcluir.id}`,
        { headers }
      );
      setSucesso(data?.message || "Entidade excluída com sucesso.");
      setEntidadeParaExcluir(null);
      setBloqueioFinanceiroExclusao(null);
      await carregar();
    } catch (err) {
      const dadosErro = err.response?.data || {};
      const mensagem =
        dadosErro.message || "Não foi possível excluir a entidade.";
      const bloqueioAsaas =
        err.response?.status === 409 &&
        (dadosErro.code === "ASAAS_OPEN_CHARGES" ||
          mensagem.includes("cobranças em aberto no Asaas"));

      if (bloqueioAsaas) {
        setBloqueioFinanceiroExclusao({ message: mensagem });
      } else {
        setErro(mensagem);
      }
    } finally {
      setExcluindo(false);
    }
  };

  const cancelarFinanceiroEExcluir = async () => {
    if (!entidadeParaExcluir) return;
    setCancelandoFinanceiro(true);
    setErro("");
    setSucesso("");
    try {
      await api.post(
        `${BASE_URL}/api/godmode/entidades/${entidadeParaExcluir.id}/cancelar-financeiro-asaas`,
        {},
        { headers }
      );
      setBloqueioFinanceiroExclusao(null);

      const { data } = await api.delete(
        `${BASE_URL}/api/godmode/entidades/${entidadeParaExcluir.id}`,
        { headers }
      );
      setSucesso(data?.message || "Entidade excluída com sucesso.");
      setEntidadeParaExcluir(null);
      await carregar();
    } catch (err) {
      setErro(
        err.response?.data?.message ||
          "Não foi possível cancelar o financeiro no Asaas e excluir a entidade."
      );
    } finally {
      setCancelandoFinanceiro(false);
    }
  };

  const testarConexao = async () => {
    setTestando(true);
    setErro("");
    setSucesso("");
    try {
      const { data } = await api.post(
        `${BASE_URL}/api/godmode/integracoes/asaas/testar`,
        { ambiente },
        { headers }
      );
      setSucesso(data?.message || "Conexão realizada com sucesso.");
      setConfigurado(true);
    } catch (err) {
      setErro(
        err.response?.data?.message ||
          "Não foi possível conectar ao Asaas."
      );
    } finally {
      setTestando(false);
    }
  };

  return (
    <div>
      <Card className="mb-3 godmode-clientes-card">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label>Ambiente Asaas</Form.Label>
              <Form.Select
                value={ambiente}
                onChange={(event) => {
                  setAmbiente(event.target.value);
                  setSucesso("");
                  setErro("");
                }}
              >
                <option value="DESENVOLVIMENTO">Desenvolvimento</option>
                <option value="PRODUCAO">Produção</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Situação</Form.Label>
              <Form.Select
                value={filtroSituacao}
                onChange={(event) => setFiltroSituacao(event.target.value)}
              >
                <option value="">Todas</option>
                <option value="A">Ativa</option>
                <option value="I">Inativa</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Localizar cliente</Form.Label>
              <Form.Control
                value={busca}
                onChange={(event) => setBusca(event.target.value)}
                placeholder="Nome, CNPJ, ID ou identificador Asaas"
              />
            </Col>
            <Col md={2} className="d-flex gap-2">
              <Button
                variant="outline-primary"
                onClick={testarConexao}
                disabled={testando}
              >
                {testando ? "Testando…" : "Testar conexão"}
              </Button>
              <Button variant="outline-secondary" onClick={carregar}>
                Atualizar
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!configurado && !loading && (
        <Alert variant="warning">
          A chave do ambiente <strong>{ambiente.toLowerCase()}</strong> não
          está configurada no backend. Configure a variável correspondente
          antes de integrar clientes.
        </Alert>
      )}

      {erro && <Alert variant="danger">{erro}</Alert>}
      {sucesso && <Alert variant="success">{sucesso}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">
              {total} cliente(s) — página {pagina} de {totalPaginas || 1}
            </small>
          </div>

          <Card className="godmode-clientes-card">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Clientes</span>
            </Card.Header>
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Cliente</th>
                    <th>CNPJ</th>
                    <th>Situação</th>
                    <th>E-mail de cobrança</th>
                    <th>Mensalidade Abonada</th>
                    <th>Integração Asaas</th>
                    <th>Cliente Asaas</th>
                    <th>Última sincronização</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {entidades.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center text-muted py-4">
                        Nenhum cliente encontrado.
                      </td>
                    </tr>
                  ) : (
                    entidades.map((entidade) => {
                      const status =
                        entidade.status_sincronizacao || "NAO_INTEGRADO";
                      return (
                        <tr key={entidade.id}>
                          <td>{entidade.id}</td>
                          <td>
                            <strong>{nomeEntidade(entidade)}</strong>
                            {entidade.razao_social &&
                              entidade.razao_social !== entidade.fantasia && (
                                <small className="d-block text-muted">
                                  {entidade.razao_social}
                                </small>
                              )}
                            {entidade.ultimo_erro_mensagem && (
                              <small
                                className="d-block text-danger"
                                title={entidade.ultimo_erro_mensagem}
                              >
                                {entidade.ultimo_erro_mensagem}
                              </small>
                            )}
                          </td>
                          <td>{entidade.cnpj ? String(entidade.cnpj).replace(/\D/g, "").replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : "—"}</td>
                          <td>
                            <Badge
                              bg={
                                String(entidade.situacao).toUpperCase() === "A"
                                  ? "success"
                                  : "secondary"
                              }
                            >
                              {String(entidade.situacao || "—").toUpperCase()}
                            </Badge>
                          </td>
                          <td>
                            {entidade.email_cobranca ||
                              entidade.email ||
                              "—"}
                          </td>
                          <td>
                            <Form.Check
                              type="switch"
                              id={`abonada-${entidade.id}`}
                              checked={entidade.mensalidade_abonada === "S"}
                              onChange={() => toggleAbonada(entidade)}
                              label={entidade.mensalidade_abonada === "S" ? "SIM" : "NÃO"}
                            />
                          </td>
                          <td>
                            <Badge bg={BADGES_STATUS[status] || "secondary"}>
                              {rotuloStatusIntegracao(status)}
                            </Badge>
                          </td>
                          <td>{entidade.asaas_customer_id || "—"}</td>
                          <td>
                            {formatarDataHora(
                              entidade.data_ultima_sincronizacao
                            )}
                          </td>
                          <td className="text-end">
                            <Button
                              size="sm"
                              variant={
                                status === "ERRO"
                                  ? "outline-danger"
                                  : "outline-primary"
                              }
                              onClick={() => abrirSincronizacao(entidade)}
                              disabled={!configurado}
                            >
                              {status === "NAO_INTEGRADO"
                                ? "Integrar"
                                : status === "ERRO"
                                  ? "Reprocessar"
                                  : "Sincronizar"}
                            </Button>{" "}
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => abrirExclusao(entidade)}
                            >
                              Excluir
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </Card>

          {totalPaginas > 1 && (
            <Pagination className="justify-content-center mt-3 godmode-clientes-pagination">
              <Pagination.Prev
                disabled={pagina <= 1}
                onClick={() => setPagina((p) => Math.max(1, p - 1))}
              />
              <Pagination.Item active>{pagina}</Pagination.Item>
              <Pagination.Next
                disabled={pagina >= totalPaginas}
                onClick={() =>
                  setPagina((p) => Math.min(totalPaginas, p + 1))
                }
              />
            </Pagination>
          )}
        </>
      )}

      <Modal
        show={selecionada != null}
        onHide={() => !salvando && setSelecionada(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Sincronizar cliente com o Asaas</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{selecionada && nomeEntidade(selecionada)}</strong>
            <br />
            <span className="text-muted">
              Referência externa: {selecionada?.id}
            </span>
          </p>

          <Form.Group className="mb-3">
            <Form.Label>Nome do grupo no Asaas</Form.Label>
            <Form.Control
              value={preferencias.nome_grupo}
              onChange={(event) =>
                setPreferencias((anterior) => ({
                  ...anterior,
                  nome_grupo: event.target.value,
                }))
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>E-mails adicionais</Form.Label>
            <Form.Control
              value={preferencias.emails_adicionais}
              onChange={(event) =>
                setPreferencias((anterior) => ({
                  ...anterior,
                  emails_adicionais: event.target.value,
                }))
              }
              placeholder="financeiro@cliente.com,contato@cliente.com"
            />
            <Form.Text>Separe múltiplos endereços por vírgula.</Form.Text>
          </Form.Group>

          <Form.Check
            type="switch"
            label="Desabilitar notificações automáticas do Asaas"
            checked={preferencias.notificacoes_desabilitadas}
            onChange={(event) =>
              setPreferencias((anterior) => ({
                ...anterior,
                notificacoes_desabilitadas: event.target.checked,
              }))
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => setSelecionada(null)}
            disabled={salvando}
          >
            Cancelar
          </Button>
          <Button onClick={sincronizar} disabled={salvando}>
            {salvando ? "Sincronizando…" : "Confirmar sincronização"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={entidadeParaExcluir != null}
        onHide={fecharExclusao}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Excluir entidade</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Tem certeza que deseja excluir{" "}
            <strong>
              {entidadeParaExcluir && nomeEntidade(entidadeParaExcluir)}
            </strong>
            ?
          </p>
          <p className="text-danger mb-0">
            Esta ação é <strong>irreversível</strong> e apaga todos os
            documentos, filiais, fornecedores, usuários exclusivos,
            configurações, logs e histórico financeiro desta entidade.
          </p>
          {bloqueioFinanceiroExclusao && (
            <Alert variant="warning" className="mt-3 mb-0">
              <strong>Financeiro pendente no Asaas.</strong>
              <div className="mt-1">
                {bloqueioFinanceiroExclusao.message}
              </div>
              <div className="mt-2">
                Para continuar, o sistema pode excluir as cobranças abertas no
                Asaas e depois remover as assinaturas vinculadas. Pagamentos já
                quitados não são estornados por esta ação.
              </div>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={fecharExclusao}
            disabled={excluindo || cancelandoFinanceiro}
          >
            Não
          </Button>
          {bloqueioFinanceiroExclusao ? (
            <Button
              variant="warning"
              onClick={cancelarFinanceiroEExcluir}
              disabled={cancelandoFinanceiro}
            >
              {cancelandoFinanceiro
                ? "Cancelando financeiro..."
                : "Cancelar financeiro no Asaas e excluir"}
            </Button>
          ) : (
            <Button
              variant="danger"
              onClick={excluirEntidade}
              disabled={excluindo}
            >
              {excluindo ? "Excluindo…" : "Sim, excluir"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ClientesGodMode;
